import { apiClient } from './api/client';

export interface AnalyticsEvent {
  session_id: string;
  user_id?: number | null;
  event_type: 'page_view' | 'interaction' | 'ecommerce';
  event_name: string;
  page_url?: string;
  page_title?: string;
  metadata?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  country?: string;
  city?: string;
}

export interface CartData {
  session_id: string;
  user_id?: number | null;
  items: Array<{
    id: number;
    name: string;
    price: number;
    quantity: number;
  }>;
  total_amount: number;
  status?: 'active' | 'abandoned' | 'converted';
}

export interface KPIs {
  visitors: number;
  uniqueUsers: number;
  pageViews: number;
  paidCustomers: number;
  paidOrders: number;
  paidOrdersTotal: number;
  avgOrderValue: number;
  conversionRate: number;
  abandonedCarts: number;
  abandonedValue: number;
  avgAbandonedCart: number;
}

export interface TimeSeriesData {
  date: string;
  visitors: number;
  purchases: number;
  carts_created: number;
}

export interface SessionData {
  id?: string;
  session_id: string;
  user_id: number | null;
  first_visit: string;
  last_visit: string;
  event_count: number;
  country: string | null;
  city: string | null;
  pages: Array<{
    url: string;
    title: string;
    timestamp: string;
  }>;
  has_cart: boolean;
  has_purchase: boolean;
  cart_value: number | null;
  cart_status: string | null;
}

class AnalyticsTracker {
  private sessionId: string;
  private userId: number | null = null;

  constructor() {
    // Générer ou récupérer l'ID de session
    this.sessionId = this.getOrCreateSessionId();
    this.loadUserId();
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  private loadUserId() {
    // Essayer de récupérer l'ID utilisateur depuis le contexte d'auth
    try {
      const authData = localStorage.getItem('auth_user');
      if (authData) {
        const user = JSON.parse(authData);
        this.userId = user?.id || null;
      }
    } catch (e) {
      // Ignorer les erreurs
    }
  }

  setUserId(userId: number | null) {
    this.userId = userId;
  }

  /**
   * Track un événement page_view
   */
  async trackPageView(pageUrl: string, pageTitle: string) {
    try {
      await apiClient.post('/analytics/track', {
        session_id: this.sessionId,
        user_id: this.userId,
        event_type: 'page_view',
        event_name: 'page_view',
        page_url: pageUrl,
        page_title: pageTitle,
        metadata: {
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (error) {
      // Ne pas bloquer l'application en cas d'erreur de tracking
      console.error('Erreur tracking page_view:', error);
    }
  }

  /**
   * Track un événement add_to_cart
   */
  async trackAddToCart(productId: number, productName: string, price: number, quantity: number) {
    try {
      await apiClient.post('/analytics/track', {
        session_id: this.sessionId,
        user_id: this.userId,
        event_type: 'ecommerce',
        event_name: 'add_to_cart',
        page_url: window.location.href,
        page_title: document.title,
        metadata: {
          product_id: productId,
          product_name: productName,
          price,
          quantity,
        },
      });
    } catch (error) {
      console.error('Erreur tracking add_to_cart:', error);
    }
  }

  /**
   * Track un événement begin_checkout
   */
  async trackBeginCheckout(cartTotal: number, itemCount: number) {
    try {
      await apiClient.post('/analytics/track', {
        session_id: this.sessionId,
        user_id: this.userId,
        event_type: 'ecommerce',
        event_name: 'begin_checkout',
        page_url: window.location.href,
        page_title: document.title,
        metadata: {
          cart_total: cartTotal,
          item_count: itemCount,
        },
      });
    } catch (error) {
      console.error('Erreur tracking begin_checkout:', error);
    }
  }

  /**
   * Track un événement purchase
   */
  async trackPurchase(orderId: number, orderTotal: number, items: any[]) {
    try {
      await apiClient.post('/analytics/track', {
        session_id: this.sessionId,
        user_id: this.userId,
        event_type: 'ecommerce',
        event_name: 'purchase',
        page_url: window.location.href,
        page_title: document.title,
        metadata: {
          order_id: orderId,
          order_total: orderTotal,
          items,
        },
      });
    } catch (error) {
      console.error('Erreur tracking purchase:', error);
    }
  }

  /**
   * Track ou met à jour un panier
   */
  async trackCart(items: Array<{ id: number; name: string; price: number; quantity: number }>) {
    try {
      const totalAmount = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      
      await apiClient.post('/analytics/track/cart', {
        session_id: this.sessionId,
        user_id: this.userId,
        items,
        total_amount: totalAmount,
        status: 'active',
      });
    } catch (error) {
      console.error('Erreur tracking cart:', error);
    }
  }

  /**
   * Marque un panier comme abandonné
   */
  async markCartAbandoned() {
    try {
      const response = await fetch('/api/analytics/track/cart/abandoned', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          session_id: this.sessionId,
        }),
      });
      if (!response.ok) {
        throw new Error('Erreur lors du marquage du panier abandonné');
      }
    } catch (error) {
      console.error('Erreur marquage panier abandonné:', error);
    }
  }
}

// Instance singleton
export const analyticsTracker = new AnalyticsTracker();

// Service pour récupérer les données analytics
export const analyticsService = {
  async getKPIs(dateFrom?: string, dateTo?: string): Promise<KPIs> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const queryString = params.toString();
    return await apiClient.get<KPIs>(`/analytics/kpis${queryString ? `?${queryString}` : ''}`);
  },

  async getTimeSeries(dateFrom?: string, dateTo?: string, groupBy: 'day' | 'week' | 'month' = 'day'): Promise<TimeSeriesData[]> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    params.append('group_by', groupBy);
    return await apiClient.get<TimeSeriesData[]>(`/analytics/time-series?${params.toString()}`);
  },

  async getSessions(dateFrom?: string, dateTo?: string, limit: number = 100): Promise<SessionData[]> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    params.append('limit', limit.toString());
    return await apiClient.get<SessionData[]>(`/analytics/sessions?${params.toString()}`);
  },
};
